import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import TrackingDetailsModal from '../components/TrackingDetailsModal';
import CancelShipmentModal from '../components/CancelShipmentModal';
import PageHeader from '../components/PageHeader';
import OrderFilterBar from '../components/orders/OrderFilterBar';
import OrdersTable from '../components/orders/OrdersTable';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { useBillableNavigationLock } from '../hooks/useBillableNavigationLock';
import { ordersApi, trackingApi, shipmentsApi } from '../api/client';
import {
  getEffectiveRefundStatus,
  getRefundUpdateTimestamp,
  mergeFilteredRecentOrders,
  updateRecentOrderStatus,
} from '../utils/recentOrders';
import { shouldApplyLocalRefundOverride } from '../utils/refunds';
import type { OrderHistoryEntry, TrackingInfo, DateFilters } from '../types';

interface LocalOrderOverride {
  status?: {
    at: string;
    patch: Partial<OrderHistoryEntry>;
  };
  tracking?: {
    at: string;
    patch: Partial<OrderHistoryEntry>;
  };
}

function timestamp(value?: string | null): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function OrderHistoryPage() {
  const { toasts, removeToast, success, error, warning, info } = useToast();

  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DateFilters>({});
  // Bumped whenever a cancel/tracking handler writes a local override, so the
  // `orders` memo re-derives. We do NOT patch the query cache: applying the
  // patch into the cache would make applyLocalOrderOverrides run over
  // already-patched data and immediately prune the override (equal timestamps),
  // reverting the change on the next background refetch.
  const [overridesVersion, setOverridesVersion] = useState(0);
  const trackingRequestRef = useRef(0);
  const localOrderOverridesRef = useRef(new Map<string, LocalOrderOverride>());

  const ordersQuery = useInfiniteQuery({
    queryKey: ['orders', filters],
    queryFn: ({ pageParam, signal }) => ordersApi.list(filters, { limit: 200, cursor: pageParam, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    // Silent background refresh; TanStack pauses the timer while the tab is
    // unfocused, matching the old document.hidden gate. A refetch keeps the
    // existing rows visible until the fresh pages land (no skeleton flash),
    // and refetches every loaded page — so the old "keep older pages" juggling
    // is handled for us.
    refetchInterval: 60000,
  });
  const loading = ordersQuery.isLoading;
  const loadingMore = ordersQuery.isFetchingNextPage;

  // Tracking modal state
  const [trackingModal, setTrackingModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    order: OrderHistoryEntry | null;
    tracking: TrackingInfo | null;
  }>({
    isOpen: false,
    loading: false,
    order: null,
    tracking: null,
  });

  // Cancel modal state
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    order: OrderHistoryEntry | null;
  }>({
    isOpen: false,
    loading: false,
    order: null,
  });

  // Syncing state
  const [syncing, setSyncing] = useState(false);
  const [trackingSyncCursor, setTrackingSyncCursor] = useState<string | null>(null);

  useBillableNavigationLock(cancelModal.loading);

  const mergeOrderPages = useCallback((primary: OrderHistoryEntry[], secondary: OrderHistoryEntry[]) => {
    const seen = new Set(primary.map((order) => order.shipment_id || order.record_key));
    return [
      ...primary,
      ...secondary.filter((order) => {
        const identity = order.shipment_id || order.record_key;
        if (seen.has(identity)) return false;
        seen.add(identity);
        return true;
      }),
    ];
  }, []);

  const applyLocalOrderOverrides = useCallback((entries: OrderHistoryEntry[]) => {
    return entries.map((entry) => {
      const override = localOrderOverridesRef.current.get(entry.shipment_id);
      if (!override) return entry;

      let merged = entry;
      let keepStatus = override.status;
      let keepTracking = override.tracking;

      if (keepStatus) {
        const localCandidate = { ...entry, ...keepStatus.patch };
        if (shouldApplyLocalRefundOverride(entry, localCandidate)) {
          merged = { ...merged, ...keepStatus.patch };
        } else {
          keepStatus = undefined;
        }
      }

      if (keepTracking) {
        if (timestamp(entry.tracking_updated_at) >= timestamp(keepTracking.at)) {
          keepTracking = undefined;
        } else {
          merged = { ...merged, ...keepTracking.patch };
        }
      }

      if (keepStatus || keepTracking) {
        localOrderOverridesRef.current.set(entry.shipment_id, {
          status: keepStatus,
          tracking: keepTracking,
        });
      } else {
        localOrderOverridesRef.current.delete(entry.shipment_id);
      }

      return merged;
    });
  }, []);

  // Flatten the cursor pages (dedupe by identity), apply local cancel/tracking
  // overrides over the RAW server data, then merge in sessionStorage recent
  // orders. Re-derives when server data changes OR an override is written
  // (overridesVersion). Applying overrides over raw server data is what lets
  // the timestamp-based survival logic keep a patch until the server catches up.
  // The raw flattened list is passed alongside the overridden display list so
  // the sessionStorage pruning inside mergeFilteredRecentOrders compares
  // against un-patched server rows — comparing against locally-patched entries
  // (identical timestamps) would wrongly prune the override on the next render.
  const orders = useMemo(() => {
    const flattened = (ordersQuery.data?.pages ?? []).reduce<OrderHistoryEntry[]>(
      (acc, page) => mergeOrderPages(acc, page.orders),
      []
    );
    return mergeFilteredRecentOrders(applyLocalOrderOverrides(flattened), filters, flattened);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersQuery.data, filters, overridesVersion, mergeOrderPages, applyLocalOrderOverrides]);

  const handleLoadMore = () => {
    if (!ordersQuery.hasNextPage || ordersQuery.isFetchingNextPage) return;
    ordersQuery.fetchNextPage();
  };

  // Invalidate the tracking-modal request guard on unmount.
  useEffect(() => () => {
    trackingRequestRef.current += 1;
  }, []);

  // Surface the initial load failure once. Keyed on isLoadingError only (not the
  // error object) so the 60s refetchInterval retrying a still-failing initial
  // load doesn't re-toast every minute — isLoadingError stays true across those
  // retries, so the effect runs a single time per failure streak.
  useEffect(() => {
    if (ordersQuery.isLoadingError) {
      const message = ordersQuery.error instanceof Error ? ordersQuery.error.message : 'Failed to load orders';
      error('Load Failed', message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersQuery.isLoadingError]);

  useEffect(() => {
    if (ordersQuery.isFetchNextPageError) {
      error('Load More Failed', 'Failed to load older orders.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersQuery.isFetchNextPageError]);

  // Filters are part of the query key, so updating them auto-refetches page one.
  const handleFilterChange = (field: keyof DateFilters, value: string) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value || undefined };
      if (field === 'date' && value) {
        next.startDate = undefined;
        next.endDate = undefined;
      } else if ((field === 'startDate' || field === 'endDate') && value) {
        next.date = undefined;
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleViewTracking = async (order: OrderHistoryEntry) => {
    if (!order.tracking_code) {
      error('No Tracking', 'This shipment does not have tracking information.');
      return;
    }

    const requestId = ++trackingRequestRef.current;
    setTrackingModal({
      isOpen: true,
      loading: true,
      order,
      tracking: null,
    });

    try {
      const tracking = await trackingApi.getByShipment(order.shipment_id);
      if (requestId !== trackingRequestRef.current) return;
      setTrackingModal((prev) => ({
        ...prev,
        loading: false,
        tracking,
      }));

      // Sync tracking status to backend and update local state
      trackingApi.syncOne(order.shipment_id).catch(() => {
        // Silently ignore sync errors - the modal still shows correct data
      });

      const trackingUpdatedAt = tracking.updated_at || new Date().toISOString();
      const existingOverride = localOrderOverridesRef.current.get(order.shipment_id) || {};
      const trackingPatch: Partial<OrderHistoryEntry> = {
        tracking_status: tracking.status,
        tracking_status_label: tracking.status_label,
        tracking_status_tone: tracking.status_tone,
        tracking_updated_at: trackingUpdatedAt,
        est_delivery_date: tracking.est_delivery_date,
      };
      localOrderOverridesRef.current.set(order.shipment_id, {
        ...existingOverride,
        tracking: { at: trackingUpdatedAt, patch: trackingPatch },
      });

      // Re-derive so the override just written above is applied over raw server
      // data; the override ref keeps it applied across background refetches.
      setOverridesVersion((v) => v + 1);
    } catch (err) {
      if (requestId !== trackingRequestRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load tracking';
      error('Tracking Error', message);
      setTrackingModal((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const closeTrackingModal = () => {
    trackingRequestRef.current += 1;
    setTrackingModal({ isOpen: false, loading: false, order: null, tracking: null });
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const result = await trackingApi.syncAll({
        force: true,
        limit: 100,
        cursor: trackingSyncCursor || undefined,
      });
      if ('queued' in result) {
        setTrackingSyncCursor(null);
        info('Tracking Sync Queued', 'Tracking updates are running in the background. New statuses will appear automatically.');
        return;
      }
      setTrackingSyncCursor(result.next_cursor);
      const continuation = result.list_complete
        ? ''
        : ' More shipment history remains; select Continue Tracking Sync to process the next page.';
      if (result.attempted === 0) {
        info(
          result.list_complete ? 'Nothing to Sync' : 'Sync Page Complete',
          `${result.skipped} shipments were not eligible for an update.${continuation}`
        );
      } else if (result.failed >= result.attempted) {
        error(
          'Sync Failed',
          `Tracking could not be updated for ${result.failed} shipments.${continuation}`
        );
      } else if (result.failed > 0) {
        warning(
          'Sync Partially Complete',
          `${result.updated} updated, ${result.failed} failed, and ${result.skipped} skipped.${continuation}`
        );
      } else {
        success(
          result.list_complete ? 'Sync Complete' : 'Sync Page Complete',
          `Updated tracking for ${result.updated} shipments.${continuation}`
        );
      }
      await queryClient.invalidateQueries({ queryKey: ['orders', filters] });
    } catch (err) {
      if ([400, 409].includes((err as { status?: number })?.status ?? 0)) {
        setTrackingSyncCursor(null);
      }
      const message = err instanceof Error ? err.message : 'Failed to sync tracking';
      error('Sync Failed', message);
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelShipment = async () => {
    const order = cancelModal.order;
    if (!order) return;

    if (!order.shipment_id) {
      error('Cancel Failed', 'A shipment ID is required to request a cancellation.');
      setCancelModal({ isOpen: false, loading: false, order: null });
      return;
    }

    setCancelModal((prev) => ({ ...prev, loading: true }));
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);

    try {
      const cancellation = await shipmentsApi.cancel(order.shipment_id, order.record_key, controller.signal);
      const returnedEntry = cancellation.entry;
      const responseState: OrderHistoryEntry = {
        ...order,
        ...(returnedEntry || {}),
        refund_status: returnedEntry?.refund_status || cancellation.refund_status || 'submitted',
      };
      const nextRefundStatus = getEffectiveRefundStatus(responseState) || 'submitted';
      const statusUpdatedAt = getRefundUpdateTimestamp(responseState) || new Date().toISOString();
      const statusPatch: Partial<OrderHistoryEntry> = {
        ...(returnedEntry || {}),
        status: returnedEntry?.status || order.status,
        refund_status: nextRefundStatus,
        status_updated_at: statusUpdatedAt,
        refund_updated_at: returnedEntry?.refund_updated_at || statusUpdatedAt,
        cancelled_at: returnedEntry?.cancelled_at || statusUpdatedAt,
      };
      const existingOverride = localOrderOverridesRef.current.get(order.shipment_id) || {};
      localOrderOverridesRef.current.set(order.shipment_id, {
        ...existingOverride,
        status: { at: statusUpdatedAt, patch: statusPatch },
      });
      setOverridesVersion((v) => v + 1);
      updateRecentOrderStatus(order.shipment_id, nextRefundStatus, statusUpdatedAt, statusPatch);
      if (nextRefundStatus === 'refunded') {
        success('Shipment Refunded', 'EasyPost reports that the refund is complete.');
      } else if (nextRefundStatus === 'rejected') {
        error('Refund Rejected', 'EasyPost rejected the refund request.');
      } else if (nextRefundStatus === 'cancelled') {
        info('Shipment Cancelled', 'EasyPost reports that the shipment is cancelled.');
      } else {
        info('Cancellation Requested', 'The refund request has been submitted.');
      }
      setCancelModal({ isOpen: false, loading: false, order: null });
    } catch (err) {
      const message = controller.signal.aborted
        ? 'The refund request timed out. Retry the same shipment to recover its authoritative refund state safely.'
        : err instanceof Error ? err.message : 'Failed to cancel shipment';
      error('Cancel Failed', message);
      setCancelModal((prev) => ({ ...prev, loading: false }));
    } finally {
      window.clearTimeout(timeoutId);
    }
  };


  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <PageHeader
        title="Orders"
        description="Every shipment, newest first · tracking auto-refreshes every 60s"
        actions={
          <button
            className={`btn btn-secondary ${syncing ? 'loading' : ''}`}
            onClick={handleSyncAll}
            disabled={syncing}
          >
            <RefreshCw size={14} />
            {syncing ? 'Syncing…' : trackingSyncCursor ? 'Continue sync' : 'Sync tracking'}
          </button>
        }
      />

      {/* Orders card: toolbar + table + footer bar */}
      <div className="card">
        <OrderFilterBar
          filters={filters}
          count={loading ? 0 : orders.length}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {loading ? (
          <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
            <div className="skeleton" style={{ height: '44px' }} />
            <div className="skeleton" style={{ height: '44px' }} />
            <div className="skeleton" style={{ height: '44px' }} />
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <p>No shipments found for the selected filters.</p>
          </div>
        ) : (
          <>
            <OrdersTable
              orders={orders}
              onViewTracking={handleViewTracking}
              onCancel={(order) => setCancelModal({ isOpen: true, loading: false, order })}
            />
            <div className="card-foot-bar">
              <span className="count">
                Showing {orders.length} shipment{orders.length !== 1 ? 's' : ''}
              </span>
              {ordersQuery.hasNextPage ? (
                <button
                  type="button"
                  className={`btn btn-secondary btn-small ${loadingMore ? 'loading' : ''}`}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              ) : (
                <span className="count">All matching shipments are shown.</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Tracking Modal */}
      <TrackingDetailsModal
        isOpen={trackingModal.isOpen}
        loading={trackingModal.loading}
        tracking={trackingModal.tracking}
        onClose={closeTrackingModal}
      />

      <CancelShipmentModal
        isOpen={cancelModal.isOpen}
        loading={cancelModal.loading}
        order={cancelModal.order}
        onClose={() => setCancelModal({ isOpen: false, loading: false, order: null })}
        onConfirm={handleCancelShipment}
      />
    </>
  );
}

export default OrderHistoryPage;
